import * as React from 'react'
import { ReactElement, useMemo } from 'react'
import { useFormikContext } from 'formik'
import { DisbursementMutationDto, DisbursementOverviewDto } from '../../../config/registrationConfig'
import ModifyRegistrationRow, {
    ModifyRegistrationColumnProps,
    ModifyRegistrationColumns,
    ModifyRegistrationRowProps,
    useRegistrationRowStyles,
} from '../Registration/ModifyRegistrationRow'
import MoneyText from '../../Text/Currency/MoneyText'
import { multiplyRate } from '../../../helpers/numberHelper'
import { DisbursementListProps } from './DisbursementList'
import VatTypeCombobox, { VatTypeComboboxProps } from '../../Combobox/i18n/VatTypeCombobox'
import DisbursementTypeCombobox, { DisbursementTypeComboboxProps } from '../../Combobox/DisbursementTypeCombobox'
import useDisbursementTypes from '../../../hooks/useDisbursementTypes'
import { MoneyDto, VatType } from '../../../config/i18nConfig'
import useVatTypes from '../../../hooks/useVatTypes'
import FormikComboboxField from '../../Formik/FormikComboboxField'

type Props = ModifyRegistrationRowProps<DisbursementOverviewDto, DisbursementMutationDto, DisbursementListProps>
type ColumnProps = ModifyRegistrationColumnProps<DisbursementListProps>

function Columns(props: ColumnProps): ReactElement {
    const styles = useRegistrationRowStyles()
    const { values, setFieldValue } = useFormikContext<DisbursementMutationDto>()
    const { disbursementTypes, getDisbursementType } = useDisbursementTypes()
    const { getVatType } = useVatTypes()
    const vatType = useMemo<VatType>(() => getVatType(values.vatType), [values.vatType])
    const total = useMemo<MoneyDto>(
        () =>
            multiplyRate((vatType?.percentage ?? 0) / 100 + 1, {
                amount: values.rate,
                currency: values.currency,
            }),
        [values.rate, vatType],
    )

    switch (props.column.key) {
        case 'type':
            return (
                <FormikComboboxField<DisbursementMutationDto, DisbursementTypeComboboxProps>
                    required={disbursementTypes.length > 0}
                    fieldName={'type'}
                    component={DisbursementTypeCombobox}
                    componentProps={{
                        size: 'small',
                        onOptionSelect: (_, data) => {
                            const disbursementType = getDisbursementType(data.selectedOptions?.[0])
                            setFieldValue('type', data.selectedOptions?.[0])
                                .then(() => setFieldValue('vatType', disbursementType?.vatType))
                                .then(() =>
                                    setFieldValue(
                                        'description',
                                        disbursementType?.description || disbursementType?.name,
                                    ),
                                )
                        },
                        className: styles.type,
                        input: { className: styles.typeInput },
                    }}
                />
            )
        case 'vat':
            return (
                <FormikComboboxField<DisbursementMutationDto, VatTypeComboboxProps>
                    required
                    fieldName={'vatType'}
                    component={VatTypeCombobox}
                    componentProps={{
                        size: 'small',
                        className: styles.vat,
                        input: { className: styles.vatInput },
                    }}
                />
            )
        case 'total':
            return <MoneyText value={total} align="right" />
    }
    return <ModifyRegistrationColumns<DisbursementMutationDto, DisbursementListProps> {...props} />
}

export default function ModifyDisbursementRow(props: Props): ReactElement {
    return (
        <ModifyRegistrationRow<DisbursementOverviewDto, DisbursementMutationDto, DisbursementListProps>
            {...props}
            columns={Columns}
        />
    )
}
